import React from 'react';
import Carousel from 'react-multi-carousel';
import styled from 'styled-components';
import { PropTypes } from 'prop-types';
import IconArrowBack from '../IconArrowBack';
import IconArrowForward from '../IconArrowForward';

const CarouselCon = styled.div`
  width: 100%;
  display: grid;

  && {
    .custom-slider {
      position: absolute;
      bottom: 0;
      width: 100%;
      padding-right: 16px;
    }
    .custom-slider__input {
      width: 100%;
      border: 0px !important;
      height: auto;
      min-height: auto;
    }
    .slider-image-item {
      padding: 20px 0 20px 20px;
    }
  }
`;

const StyledInput = styled('input')`
  -webkit-appearance: none;

  &::-webkit-slider-runnable-track {
    width: 300px;
    height: 5px;
    background: white;
    border: none;
    border-radius: 3px;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    border: none;
    height: 5px;
    width: ${props => props.thumbWidth}% !important;
    border-radius: 3px;
    background: ${props => props.thumbColor} !important;
  }

  &:focus {
    outline: none;
  }

  &:focus::-webkit-slider-runnable-track {
    background: white;
  }
`;

const ButtonGroup = styled.div`
  position: absolute;
  right: 0;
  top: 0px;
  padding-top: 8px;
  display: flex;
  gap: 4px;
  outline: none;
  border: none;
  background: transparent;

  > div {
    padding: 1px 6px;
  }

  div {
    outline: none;
    border: none;
    background: transparent;
    cursor: pointer;

    &:hover,
    &:active,
    &:focus {
      background: transparent;
      border: none;
      outline: none !important;
    }
  }
`;

const CarouselWithScrollbar = ({
  isMobile,
  countOfItemsInOffer,
  responsive,
  children,
  thumbColor,
  isCarouselArrowsAlwaysVisible,
  arrowsWithTitle,
  carousalStyleProps,
  carouselRef,
}) => (
  <CarouselCon>
    <Carousel
      ssr={false}
      ref={carouselRef}
      customButtonGroup={
        countOfItemsInOffer <= 1 ? null : (
          <CustomSlider
            carouselRef={carouselRef.current}
            numberOfItemsInSlide={
              isMobile ? responsive.mobile.items : responsive.desktop.items
            }
            countOfItemsInOffer={countOfItemsInOffer}
            thumbColor={thumbColor}
            arrowsWithTitle={arrowsWithTitle && !isCarouselArrowsAlwaysVisible}
            carousalStyleProps={carousalStyleProps}
          />
        )
      }
      responsive={responsive}
      containerClass="carousel-container-with-scrollbar"
      className="mr-products-container-carousel mr-products-container-carousel--with-dots"
      dotListClass="mr-products-container-carousel__dot-list"
      renderButtonGroupOutside
      arrows={isCarouselArrowsAlwaysVisible}
    >
      {children}
    </Carousel>
  </CarouselCon>
);

CarouselWithScrollbar.propTypes = {
  isMobile: PropTypes.bool,
  countOfItemsInOffer: PropTypes.number,
  responsive: PropTypes.object,
  children: PropTypes.node,
  thumbColor: PropTypes.string,
  isCarouselArrowsAlwaysVisible: PropTypes.bool,
  arrowsWithTitle: PropTypes.bool,
  carouselRef: PropTypes.any,
};

const CustomSlider = ({
  carouselState,
  carouselRef,
  numberOfItemsInSlide,
  thumbColor,
  goToSlide,
  countOfItemsInOffer,
  next,
  previous,
  carousalStyleProps,
  arrowsWithTitle,
}) => {
  let value = 0;
  let carouselItemWidth = 0;

  if (carouselRef) {
    carouselItemWidth = carouselRef.state.itemWidth;
    const maxTranslateX = Math.round(
      carouselItemWidth *
        (carouselRef.state.totalItems - carouselRef.state.slidesToShow) +
        0,
    );
    value = maxTranslateX / 100; // calculate the unit of transform for the slider
  }
  const { transform, currentSlide, totalItems } = carouselState;

  const totalNumberOfSlides =
    countOfItemsInOffer - Math.floor(numberOfItemsInSlide);

  const isAllItemsVisibleInASingleSlide =
    countOfItemsInOffer <= numberOfItemsInSlide;

  const nextDisable =
    totalNumberOfSlides === Math.ceil(currentSlide) ||
    isAllItemsVisibleInASingleSlide;

  // back button will be disabled if the we are currently at the first slide or if all items are visible in a single slide where, there won't be any need to scroll
  const isDisableBackButton =
    currentSlide === 0 || isAllItemsVisibleInASingleSlide;

  // next button will be disabled if we are either at the very last slide or all items are visible in a single slide.
  const isDisableNextButton = currentSlide === totalItems - 1 || nextDisable;

  const handlePrevious = e => {
    // can't disable a div, adding manual condition for that
    if (!isDisableBackButton) {
      e.preventDefault();
      e.stopPropagation();
      previous();
    }
  };

  const handleNext = e => {
    // can't disable a div, adding manual condition for that
    if (!isDisableNextButton) {
      e.preventDefault();
      e.stopPropagation();
      if (totalNumberOfSlides - currentSlide === 1) next();
      else goToSlide(currentSlide + 1);
    }
  };
  // Carousel arrows will be hidden if both the arrows are disabled
  const isHideArrows = isDisableBackButton && isDisableNextButton;

  function getArrowColor(type) {
    if (carousalStyleProps) {
      if (
        carousalStyleProps.desktopLayoutType === 'centered' ||
        carousalStyleProps.desktopLayoutType === 'compact'
      ) {
        return carousalStyleProps[type];
      }
      if (carousalStyleProps.isEmbeddedCarouselEnabled) {
        return carousalStyleProps[type];
      }
      return '#333030';
    }
    return '#333030';
  }

  return (
    <>
      {arrowsWithTitle &&
        !isHideArrows && (
          <ButtonGroup>
            <div onClick={handlePrevious} disabled={isDisableBackButton}>
              <IconArrowBack
                color={
                  isDisableBackButton
                    ? '#ABABAB'
                    : getArrowColor('carouselArrowColor')
                }
              />
            </div>
            <div onClick={handleNext} disabled={isDisableNextButton}>
              <IconArrowForward
                color={
                  isDisableNextButton
                    ? '#ABABAB'
                    : getArrowColor('carouselArrowColor')
                }
              />
            </div>
          </ButtonGroup>
        )}

      {!isAllItemsVisibleInASingleSlide && (
        <div className="custom-slider">
          <StyledInput
            type="range"
            value={Math.round(Math.abs(transform) / value)}
            defaultValue={0}
            onChange={e => {
              if (carouselRef.isAnimationAllowed) {
                carouselRef.isAnimationAllowed = false;
              }
              const nextTransform = e.target.value * value;
              const nextSlide = Math.round(nextTransform / carouselItemWidth);
              carouselRef.setState({
                transform: -nextTransform, // padding 20px and 5 items.
                currentSlide: nextSlide,
              });
            }}
            className="custom-slider__input"
            thumbWidth={Math.min(
              (100 * numberOfItemsInSlide) / countOfItemsInOffer,
              100,
            )}
            thumbColor={getArrowColor('carouselSliderColor')}
          />
        </div>
      )}
    </>
  );
};

CustomSlider.propTypes = {
  carouselState: PropTypes.object,
  carouselRef: PropTypes.object,
  numberOfItemsInSlide: PropTypes.number,
  thumbColor: PropTypes.string,
  goToSlide: PropTypes.func,
  countOfItemsInOffer: PropTypes.number,
  next: PropTypes.func,
  previous: PropTypes.func,
  arrowsWithTitle: PropTypes.bool,
};

export default CarouselWithScrollbar;
